import { useCallback, useMemo, useState } from "react";
import { ObraHeaderInfo } from "../../domain/models/obra_header_info";
import { useObrasCommonReports } from "../components/hooks/obras_common_reports";
import { useObrasTopHeaderState } from "../components/hooks/obras_top_header_state";
import { useLocation } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { ObraChecklistServicoHeader } from "../../domain/models/obra_checklist_servico_header";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { ObraChecklistServicoEstatisticas } from "../../domain/models/obra_checklist_servico_estatiticas";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { useObraItensChecklistAreaVerificacaoPagination } from "../hooks/ObraItensChecklistArea_verificacao/pagination";
import { ObraChecklistServicoCampos } from "../../domain/models/obra_checklist_servico_campos";
import { InspecaoObraDetalhes } from "../../domain/models/inspecao_obra_detalhes";
import { ExecutadoPor } from "../../domain/models/executador_por";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { FuncionarioDropDown } from "../../../../common/domain/models/funcionario_dropdown";
import { useForm } from "react-hook-form";
import { InspecaoObraVerificacaoForm, InspecaoObraVerificacaoSchema } from "../schemas/ObraItensChecklistArea_verificacao/inspecao_obra_verificacao";
import { zodResolver } from "@hookform/resolvers/zod";
import { InspecaoObraOcorrencia } from "../../domain/models/inspecao_obra_ocorrencia";
import { InspecaoObraOcorrenciaForm } from "../schemas/ObraItensChecklistArea_verificacao/inspecao_obra_ocorrencia";
import { InspecaoObraItem } from "../../domain/models/inspecao_obra_item";
import { FornecedorDropDown } from "../../../../common/domain/models/fornecedor_dropdown";
import { FuncionarioTercDropDown } from "../../../../common/domain/models/funcionario_terc_dropdown";
import { InspecaoObraExecutadoPorForm } from "../schemas/ObraItensChecklistArea_verificacao/inspecao_obra_executado_por";
import { useUserAuthStore } from "../../../../stores/global/userAuth";
import { GetAllAtributosObrasCadastroResponse } from "../../data/daos/read/get_all_atributos_obras_cadastro";

import dayjs from "dayjs";

/**
 * LoadInspecaoObraItensFuncState
 */

type LoadInspecaoObraItensFuncState = {
  idInspecaoObra?: number;
  page?: number;
  rowsPerPage?: number;
};

/**
 * LoadExecutadoPorFuncState
 */

type LoadExecutadoPorFuncState = {
  idInspecaoObra?: number;
  page?: number;
  rowsPerPage?: number;
}

/**
 * LoadOcorrenciasFuncState
 */

type LoadOcorrenciasFuncState = {
  idInspecaoObra?: number;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useObraItensChecklistAreaVerificacaoViewModel
 */

export const useObraItensChecklistAreaVerificacaoViewModel = () => {
  const location = useLocation();

  const { idUsuario } = useUserAuthStore();

  const {
    commons: {
      getAllLogAlteracaoByParentUseCase,
      getAllLogAlteracaoUseCase,
      getFuncionarioByIdUsuarioUseCase,
    },
    obras: {
      getAllAtributosObrasCadastroUseCase,
      getObraHeaderInfoUseCase,
      getObraChecklistServicoHeaderUseCase,
      getObraChecklistServicoStatisticasUseCase,
      getObraChecklistServicoCamposUseCase,
      getInspecaoObraByIdUseCase,
      getAllInspecaoObraItemUseCase,
      getAllExecutadoPorUseCase,
      getAllInspecaoObraOcorrenciaUseCase,
      createInspecaoObraOcorrenciaUseCase,
      updateInspecaoObraOcorrenciaUseCase,
      deleteInspecaoObraOcorrenciaUseCase,
      createInspecaoObraExecutadoPorUseCase,
      deleteInspecaoObraExecutadoPorUseCase,
      updateInspecaoObraWithValidationUseCase,
      getInspecaoObraImageUseCase,
    },
  } = useDependencyInjection();

  const {
    loadFuncionariosDropDown,
    loadFornecedoresDropDown,
    loadFuncionariosTercDropDown,
  } = useDropDownLoader();

  const {
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();

  const hookForm = useForm<InspecaoObraVerificacaoForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: { filtrarFuncionariosPorCentroCusto: true, inspecaoObraItens: [] },
    resolver: zodResolver(InspecaoObraVerificacaoSchema),
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [atributoObras, setAtributoObras] = useState<GetAllAtributosObrasCadastroResponse>();

  const [fillCurrentDate, setFillCurrentDate] = useState(false);
  const [fillOcorrenciaData, setFillOcorrenciaData] = useState(false);

  const [lockFuncionarioInspecionado, setLockFuncionarioInspecionado] = useState(false);
  const [lockFuncionarioAprovado, setLockFuncionarioAprovado] = useState(false);

  const [funcionariosDropDown, setFuncionariosDropDown] = useState<FuncionarioDropDown[]>();
  const [fornecedoresDropDown, setFornecedoresDropDown] = useState<FornecedorDropDown[]>();
  const [funcionariosTercDropDown, setFuncionariosTercDropDown] = useState<FuncionarioTercDropDown[]>();

  const [idObra, setIdObra] = useState<number>();
  const [idObraAreaChecklist, setIdObraAreaChecklist] = useState<number>();
  const [idObraChecklistServico, setIdObraChecklistServico] = useState<number>();
  const [idInspecaoObra, setIdInspecaoObra] = useState<number>();

  const [inspecaoObra, setInspecaoObra] = useState<InspecaoObraDetalhes>();
  const [executadoPor, setExecutadoPor] = useState<ExecutadoPor[]>();
  const [inspecaoObraOcorrencias, setInspecoesObraOcorrencias] = useState<InspecaoObraOcorrencia[]>();
  const [inspecaoObraItensBase, setInspecaoObraItensBase] = useState<InspecaoObraItem[]>();

  const [ocorrenciaModal, setOcorrenciaModal] = useState(false);
  const [executadoPorModal, setExecutadoPorModal] = useState(false);
  const [deleteOcorrenciaModal, setDeleteOcorrenciaModal] = useState(false);
  const [deleteExecutadoPorModal, setDeleteExecutadoPorModal] = useState(false);

  const [selectedOcorrencia, setSelectedOcorrencia] = useState<InspecaoObraOcorrencia>();
  const [selectedInspecaoObraItem, setSelectedInspecaoObraItem] = useState<InspecaoObraItem>();
  const [selectedInspecaoObraExecutadoPor, setSelectedInspecaoObraExecutadoPor] = useState<ExecutadoPor>();

  const [estatisticasChecklistServico, setEstatisticasChecklistServico] = useState<ObraChecklistServicoEstatisticas>();
  const [obraChecklistServicoCampos, setObraChecklistServicoCampos] = useState<ObraChecklistServicoCampos>();

  const [obraChecklistServicoHeader, setObraChecklistServicoHeader] = useState<ObraChecklistServicoHeader>();

  const [obrasReportState, loadObrasCommonReportsData] = useObrasCommonReports({ idObra });
  const [selectedObraHeaderInfo, setSelectedObraHeaderInfo] = useState<ObraHeaderInfo>();

  const obrasTopHeaderState = useObrasTopHeaderState();

  const pagination = useObraItensChecklistAreaVerificacaoPagination();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase,
    onSetError: e => pushErrorNotification("Erro", [e]),
  });

  const [inspecaoObraItens] = hookForm.watch(['inspecaoObraItens']);

  const filteredFuncionariosDropdown = useMemo(() => {
    if (funcionariosDropDown === undefined) return null;
    return funcionariosDropDown.filter(x => x.canBeSelected);
  }, [
    funcionariosDropDown,
  ]);

  const lockInspecaoObraItens = useMemo(() => {
    return inspecaoObra && inspecaoObra.status !== 0 && inspecaoObra.status !== 3;
  }, [
    inspecaoObra,
  ]);

  /**
   * Funcoes de carregamento
   */

  const loadAtributoObras = useCallback(async () => {
    try {
      const response = await getAllAtributosObrasCadastroUseCase.getAllAtributosObrasCadastro();
      setAtributoObras(response);
    } catch (e: unknown) {
      setAtributoObras(undefined);
      throw new Error('Falha ao buscar atributos de obras!');
    }
  }, [
    getAllAtributosObrasCadastroUseCase,
  ]);

  const loadObraHeaderInfo = useCallback(async (id?: number) => {
    try {
      const filteredIdObra = id ?? idObra;
      const response = await getObraHeaderInfoUseCase.getObraHeaderInfo(filteredIdObra);
      setSelectedObraHeaderInfo(response);
    } catch (e: unknown) {
      setSelectedObraHeaderInfo(undefined);
      throw new Error('Falha ao buscar dados da obra!');
    }
  }, [
    idObra,
    getObraHeaderInfoUseCase,
  ]);

  const loadObraChecklistServicoHeader = useCallback(async (id?: number) => {
    try {
      const filteredIdObraChecklistServico = id ?? idObraChecklistServico;
      const response = await getObraChecklistServicoHeaderUseCase.getObraChecklistServicoHeader(filteredIdObraChecklistServico);
      setObraChecklistServicoHeader(response);
    } catch (e: unknown) {
      setSelectedObraHeaderInfo(undefined);
      throw new Error('Falha ao buscar dados da obra!');
    }
  }, [
    idObraChecklistServico,
    getObraChecklistServicoHeaderUseCase,
  ]);

  const loadObraChecklistServicoEstatisticas = useCallback(async (id?: number) => {
    try {
      const filteredIdObraChecklistServico = id ?? idObraChecklistServico;

      const response = await getObraChecklistServicoStatisticasUseCase.getObraChecklistServicoStatisticas(
        filteredIdObraChecklistServico,
      );

      setEstatisticasChecklistServico(response);
    } catch (e: unknown) {
      setEstatisticasChecklistServico(undefined);
      throw new Error("Falha ao buscar dados da pagina!");
    }
  }, [
    idObraChecklistServico,
    getObraChecklistServicoStatisticasUseCase,
  ]);

  const loadObraChecklistServicoCampos = useCallback(async (id?: number) => {
    try {
      const filteredIdObraChecklistServico = id ?? idObraChecklistServico;
      const response = await getObraChecklistServicoCamposUseCase
        .getObraChecklistServicoCampos(filteredIdObraChecklistServico);
      setObraChecklistServicoCampos(response);
    } catch (e: unknown) {
      setObraChecklistServicoCampos(undefined);
      throw new Error('Falha ao buscar informações sobre o usuário!');
    }
  }, [
    idObraChecklistServico,
    getObraChecklistServicoCamposUseCase,
  ]);

  const loadInspecaoObra = useCallback(async (id?: number) => {
    if (idUsuario) {
      try {
        const filteredIdInspecaoObra = id ?? idInspecaoObra;
        const result = await getInspecaoObraByIdUseCase.getInspecaoObraById(filteredIdInspecaoObra);

        const funcionarioResult = await getFuncionarioByIdUsuarioUseCase.getFuncionarioByIdUsuario(idUsuario);

        setLockFuncionarioInspecionado(result.status === 1 || result.status === 2);
        setLockFuncionarioAprovado(result.status === 1 || result.status === 2);

        hookForm.reset();
        hookForm.setValue('funcionarioAprovacaoFinal', result.funcionarioAprovacaoFinal);
        hookForm.setValue('dataAprovacaoFinal', result.dataAprovacaoFinal ? dayjs(result.dataAprovacaoFinal).toDate() : null);
        hookForm.setValue('idFuncionarioAprovado', result.idFuncionarioAprovado);
        hookForm.setValue('status', result.status ?? 0);
        hookForm.setValue('dataEncerramento', result.dataEncerramento ? dayjs(result.dataEncerramento).toDate() : null);
        hookForm.setValue('observacao', result.observacao);

        hookForm.setValue('descricaoArquivo1', result.descArquivo1);
        hookForm.setValue('descricaoArquivo2', result.descArquivo2);
        hookForm.setValue('descricaoArquivo3', result.descArquivo3);
        hookForm.setValue('descricaoArquivo4', result.descArquivo4);
        hookForm.setValue('descricaoArquivo5', result.descArquivo5);

        if (result.idFuncionarioInspecionado) {
          hookForm.setValue('idFuncionarioInspecionado', result.idFuncionarioInspecionado);
        } else {
          if (funcionarioResult && funcionarioResult.idFuncionario) {
            hookForm.setValue('idFuncionarioInspecionado', funcionarioResult.idFuncionario);
          }
        }

        setInspecaoObra(result);
      } catch (e: unknown) {
        setInspecaoObra(undefined);
        throw new Error('Falha ao buscar inspeção obra!');
      }
    }
  }, [
    idUsuario,
    idInspecaoObra,
    getInspecaoObraByIdUseCase,
    getFuncionarioByIdUsuarioUseCase,
    hookForm,
  ]);

  const loadInspecaoObraItens = useCallback(async (fs?: LoadInspecaoObraItensFuncState) => {
    try {
      const filteredIdInspecaoObra = fs?.idInspecaoObra ?? idInspecaoObra;
      const filteredPage = fs?.page ?? pagination.inspecaoObraItens.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.inspecaoObraItens.rowsPerPage;

      await getAllInspecaoObraItemUseCase.getAllInspecaoObraItem(
        filteredIdInspecaoObra,
        filteredPage,
        filteredRowsPerPage,
      );

      hookForm.setValue('inspecaoObraItens', getAllInspecaoObraItemUseCase.items.map(x => ({
        idInspecaoObraItem: x.idInspecaoObraItens,
        descricao: x.descricao,
        metodoVerificacao: x.metodoVerificacao,
        tolerancia: x.tolerancia,
        inspecao1: x.inspecao1 ?? '',
        dataInspecao1: x.dataInspecao1 ? dayjs(x.dataInspecao1).toDate() : null,
        inspecao2: x.inspecao2 ?? '',
        dataInspecao2: x.dataInspecao2 ? dayjs(x.dataInspecao2).toDate() : null,
        usuarioRegistradoPorInspecao1: x.usuarioRegistradoPorInspecao1,
        usuarioRegistradoPorInspecao2: x.usuarioRegistradoPorInspecao2,
        dataRegistradoPorInspecao1: x.dataRegistradoPorInspecao1,
        dataRegistradoPorInspecao2: x.dataRegistradoPorInspecao2,
        path: x.path,
      })));

      setInspecaoObraItensBase(getAllInspecaoObraItemUseCase.items);

      pagination.inspecaoObraItensSetter.setTotalItems(getAllInspecaoObraItemUseCase.totalItems);
    } catch (e: unknown) {
      hookForm.setValue('inspecaoObraItens', []);
      throw new Error('Falha ao buscar itens de inspeção de obra!');
    }
  }, [
    hookForm,
    idInspecaoObra,
    pagination,
    getAllInspecaoObraItemUseCase,
  ]);

  const loadExecutadoPor = useCallback(async (fs?: LoadExecutadoPorFuncState) => {
    try {
      const filteredIdInspecaoObra = fs?.idInspecaoObra ?? idInspecaoObra;
      const filteredPage = fs?.page ?? pagination.executadoPor.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.executadoPor.rowsPerPage;

      await getAllExecutadoPorUseCase.getAllExecutadoPor(
        filteredIdInspecaoObra,
        filteredPage,
        filteredRowsPerPage,
      );

      setExecutadoPor(getAllExecutadoPorUseCase.items);
      pagination.executadoPorSetter.setTotalItems(getAllExecutadoPorUseCase.totalItems);
    } catch (e: unknown) {
      setExecutadoPor(undefined);
      throw new Error('Falha ao buscar itens de inspeção de obra!');
    }
  }, [
    idInspecaoObra,
    pagination,
    getAllExecutadoPorUseCase,
  ]);

  const loadOcorrencias = useCallback(async (fs?: LoadOcorrenciasFuncState) => {
    try {
      const filteredIdInspecaoObra = fs?.idInspecaoObra ?? idInspecaoObra;
      const filteredPage = fs?.page ?? pagination.inspecaoObraItens.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.inspecaoObraItens.rowsPerPage;

      await getAllInspecaoObraOcorrenciaUseCase.getAllInspecaoObraOcorrencia(
        filteredIdInspecaoObra,
        filteredPage,
        filteredRowsPerPage,
      );

      setInspecoesObraOcorrencias(getAllInspecaoObraOcorrenciaUseCase.items);
      pagination.ocorrenciasSetter.setTotalItems(getAllInspecaoObraOcorrenciaUseCase.totalItems);
    } catch (e: unknown) {
      setInspecoesObraOcorrencias(undefined);
      throw new Error('Falha ao buscar ocorrências!');
    }
  }, [
    idInspecaoObra,
    pagination,
    getAllInspecaoObraOcorrenciaUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationObraId = params.get('id');
      const locationIdChecklistArea = params.get('idChecklistArea');
      const locationIdChecklistServico = params.get('idChecklistServico');
      const locationIdInspecaoObra = params.get('idInspecaoObra');
      const locIdObra = locationObraId ?? undefined;
      const locIdChecklistArea = locationIdChecklistArea ?? undefined;
      const locIdChecklistServico = locationIdChecklistServico ?? undefined;
      const locIdInspecaoObra = locationIdInspecaoObra ?? undefined;

      if (!locIdObra || locIdObra.length === 0) {
        setErrorMessage("Falha ao recuperar id da obra!");
        return;
      }
      setIdObra(Number(locIdObra));

      if (!locIdChecklistArea || locIdChecklistArea.length === 0) {
        setErrorMessage("Falha ao recuperar id de checklist de area!");
        return;
      }
      setIdObraAreaChecklist(Number(locIdChecklistArea));

      if (!locIdChecklistServico || locIdChecklistServico.length === 0) {
        setErrorMessage("Falha ao recuperar id de checklist de serviço!");
        return;
      }
      setIdObraChecklistServico(Number(locIdChecklistServico));

      if (!locIdInspecaoObra || locIdInspecaoObra.length === 0) {
        setErrorMessage("Falha ao recuperar id de inspeção de obra!");
        return;
      }
      setIdInspecaoObra(Number(locIdInspecaoObra));

      await loadAtributoObras();

      await loadFuncionariosDropDown(dt => setFuncionariosDropDown(dt), { includeCantBeSelected: true });

      await loadFornecedoresDropDown(dt => setFornecedoresDropDown(dt));

      await loadFuncionariosTercDropDown(dt => setFuncionariosTercDropDown(dt));

      await loadObraHeaderInfo(Number(locIdObra));

      await loadObrasCommonReportsData(Number(locIdObra));

      await loadObraChecklistServicoHeader(Number(locIdChecklistServico));

      await loadObraChecklistServicoEstatisticas(Number(locIdChecklistServico));

      await loadObraChecklistServicoCampos(Number(locIdChecklistServico));

      await loadInspecaoObra(Number(locIdInspecaoObra));

      await loadInspecaoObraItens({ idInspecaoObra: Number(locIdInspecaoObra) });

      await loadExecutadoPor({ idInspecaoObra: Number(locIdInspecaoObra) });

      await loadOcorrencias({ idInspecaoObra: Number(locIdInspecaoObra) });
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados das paginas!')[0]);
    }
  });
  /**
   * Eventos
   */

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: `/Obras_cadastroarea?id=${idObra}` });
  }, [
    idObra,
    backOrNavigate,
  ]);

  const handleReload = useCallback(async () => {
    if (idObra && idObraChecklistServico && idInspecaoObra) {
      try {
        setLoading(true);

        await loadAtributoObras();

        await loadFuncionariosDropDown(dt => setFuncionariosDropDown(dt), { includeCantBeSelected: true });

        await loadFornecedoresDropDown(dt => setFornecedoresDropDown(dt));

        await loadFuncionariosTercDropDown(dt => setFuncionariosTercDropDown(dt));

        await loadObraHeaderInfo(idObra);

        await loadObrasCommonReportsData(idObra);

        await loadObraChecklistServicoHeader(idObraChecklistServico);

        await loadObraChecklistServicoEstatisticas(idObraChecklistServico);

        await loadObraChecklistServicoCampos(idObraChecklistServico);

        await loadInspecaoObra(idInspecaoObra);

        await loadInspecaoObraItens({ idInspecaoObra: idInspecaoObra });

        await loadExecutadoPor({ idInspecaoObra: idInspecaoObra });

        await loadOcorrencias({ idInspecaoObra: idInspecaoObra });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados das paginas!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    idObra,
    idObraChecklistServico,
    idInspecaoObra,
    loadAtributoObras,
    loadFuncionariosDropDown,
    loadFornecedoresDropDown,
    loadFuncionariosTercDropDown,
    loadObraHeaderInfo,
    loadObrasCommonReportsData,
    loadObraChecklistServicoHeader,
    loadObraChecklistServicoEstatisticas,
    loadObraChecklistServicoCampos,
    loadInspecaoObra,
    loadOcorrencias,
    loadInspecaoObraItens,
    loadExecutadoPor,
    pushErrorNotification,
  ]);

  const handleSubmit = useCallback(async (form: InspecaoObraVerificacaoForm) => {
    if (idObra && idInspecaoObra) {
      try {
        setSaving(true);
        setPageSaving(true);

        const dataHoje = fillCurrentDate
          ? dayjs(new Date()).startOf('day').utc().toDate()
          : null;

        const inspecaoObraItensAtualizados = form.inspecaoObraItens.map(itemForm => {
          const itemOriginal = inspecaoObraItensBase?.find(
            item => item.idInspecaoObraItens === itemForm.idInspecaoObraItem,
          );

          const inspecao1Alterada = itemOriginal !== undefined
            && (itemOriginal.inspecao1 ?? '') !== itemForm.inspecao1;
          const inspecao2Alterada = itemOriginal !== undefined
            && (itemOriginal.inspecao2 ?? '') !== itemForm.inspecao2;

          return {
            idInspecaoObraItem: itemForm.idInspecaoObraItem,
            inspecao1: itemForm.inspecao1,
            dataInspecao1: fillCurrentDate
              && inspecao1Alterada
              && itemForm.dataInspecao1 === null
              ? dataHoje
              : itemForm.dataInspecao1,
            inspecao2: itemForm.inspecao2,
            dataInspecao2: fillCurrentDate
              && inspecao2Alterada
              && itemForm.dataInspecao2 === null
              ? dataHoje
              : itemForm.dataInspecao2,
          };
        });

        await updateInspecaoObraWithValidationUseCase.updateInspecaoObraWithValidation(
          idObra,
          idInspecaoObra,
          form.idFuncionarioInspecionado,
          form.idFuncionarioAprovado,
          form.status,
          form.dataEncerramento,
          form.arquivo1,
          form.limparArquivo1,
          form.arquivo2,
          form.limparArquivo2,
          form.arquivo3,
          form.limparArquivo3,
          form.arquivo4,
          form.limparArquivo4,
          form.arquivo5,
          form.limparArquivo5,
          form.observacao,
          form.descricaoArquivo1,
          form.descricaoArquivo2,
          form.descricaoArquivo3,
          form.descricaoArquivo4,
          form.descricaoArquivo5,
          inspecaoObraItensAtualizados,
        );

        await loadInspecaoObra();
        await loadInspecaoObraItens();

        pushInfoNotification("Sucesso", "Informações salvas com sucesso!");
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao salvar inspeção obra!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idObra,
    idInspecaoObra,
    updateInspecaoObraWithValidationUseCase,
    fillCurrentDate,
    inspecaoObraItensBase,
    loadInspecaoObra,
    loadInspecaoObraItens,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleOcorrenciaModalOpenState = useCallback((idInformacoesInspecaoObra: number | null, idInspecaoObraItens: number | null, openState: boolean, fillData?: boolean) => {
    if (openState) {
      if (idInformacoesInspecaoObra && inspecaoObraOcorrencias) {
        setSelectedOcorrencia(inspecaoObraOcorrencias.find(x => x.idInformacoesInspecaoObra === idInformacoesInspecaoObra));
      } else {
        setSelectedOcorrencia(undefined);
      }
      if (idInspecaoObraItens && inspecaoObraItensBase) {
        setSelectedInspecaoObraItem(inspecaoObraItensBase.find(x => x.idInspecaoObraItens === idInspecaoObraItens));

        let computedFillData = false;
        const inspecaoItens = hookForm.getValues('inspecaoObraItens');
        if (inspecaoItens && inspecaoItens.length > 0) {
          const inspecaoItem = inspecaoItens.find(x => x.idInspecaoObraItem === idInspecaoObraItens);
          if (inspecaoItem) {
            const { inspecao1 } = inspecaoItem;
            if (inspecao1) {
              const normalizedInspecao1 = inspecao1.toLowerCase();
              if (normalizedInspecao1 === 'x' || normalizedInspecao1 === 'r') {
                computedFillData = fillData ?? false;
              }
            }
          }
        }
        setFillOcorrenciaData(computedFillData);
      } else {
        setSelectedInspecaoObraItem(undefined);
      }
    }
    setOcorrenciaModal(openState);
  }, [
    hookForm,
    inspecaoObraOcorrencias,
    inspecaoObraItensBase,
  ]);

  const handleInspecao1Change = useCallback((idInspecaoObraItens: number, value: string | number | null) => {
    const copiaInspecaoObraItens = [...hookForm.getValues().inspecaoObraItens];
    const inspecaoObraItemIndex = copiaInspecaoObraItens.findIndex(x => x.idInspecaoObraItem === idInspecaoObraItens);

    if (inspecaoObraItemIndex >= 0) {
      copiaInspecaoObraItens[inspecaoObraItemIndex].inspecao1 = value?.toString() ?? '';
      copiaInspecaoObraItens[inspecaoObraItemIndex].inspecao2 = '';
    }

    hookForm.setValue('inspecaoObraItens', copiaInspecaoObraItens);
  }, [
    hookForm,
  ]);

  const handleDataInspecao1Change = useCallback((idInspecaoObraItens: number, value: Date | null) => {
    const copiaInspecaoObraItens = [...hookForm.getValues().inspecaoObraItens];
    const inspecaoObraItemIndex = copiaInspecaoObraItens.findIndex(x => x.idInspecaoObraItem === idInspecaoObraItens);

    if (inspecaoObraItemIndex >= 0) {
      copiaInspecaoObraItens[inspecaoObraItemIndex].dataInspecao1 = value;
    }

    hookForm.setValue('inspecaoObraItens', copiaInspecaoObraItens);
  }, [
    hookForm,
  ]);

  const handleInspecao2Change = useCallback((idInspecaoObraItens: number, value: string | number | null) => {
    const copiaInspecaoObraItens = [...hookForm.getValues().inspecaoObraItens];
    const inspecaoObraItemIndex = copiaInspecaoObraItens.findIndex(x => x.idInspecaoObraItem === idInspecaoObraItens);

    if (inspecaoObraItemIndex >= 0) {
      copiaInspecaoObraItens[inspecaoObraItemIndex].inspecao2 = value?.toString() ?? '';
    }

    hookForm.setValue('inspecaoObraItens', copiaInspecaoObraItens);
  }, [
    hookForm,
  ]);

  const handleDataInspecao2Change = useCallback((idInspecaoObraItens: number, value: Date | null) => {
    const copiaInspecaoObraItens = [...hookForm.getValues().inspecaoObraItens];
    const inspecaoObraItemIndex = copiaInspecaoObraItens.findIndex(x => x.idInspecaoObraItem === idInspecaoObraItens);

    if (inspecaoObraItemIndex >= 0) {
      copiaInspecaoObraItens[inspecaoObraItemIndex].dataInspecao2 = value;
    }

    hookForm.setValue('inspecaoObraItens', copiaInspecaoObraItens);
  }, [
    hookForm,
  ]);

  const handleDownloadFile = useCallback(async (filename: string, imagePosition: number) => {
    if (idInspecaoObra) {
      try {
        setLoading(true);

        await getInspecaoObraImageUseCase.getInspecaoObraImage(
          idInspecaoObra,
          imagePosition,
          filename,
        );

      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao salvar inspeção obra!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    idInspecaoObra,
    getInspecaoObraImageUseCase,
    pushErrorNotification,
  ]);

  const handleDeleteFile = useCallback((fileId: number | null) => {
    if (fileId && inspecaoObra) {
      switch (fileId) {
        case 1:
          setInspecaoObra({ ...inspecaoObra, nomeArquivo1: null });
          hookForm.setValue('limparArquivo1', true);
          break;

        case 2:
          setInspecaoObra({ ...inspecaoObra, nomeArquivo2: null });
          hookForm.setValue('limparArquivo2', true);
          break;

        case 3:
          setInspecaoObra({ ...inspecaoObra, nomeArquivo3: null });
          hookForm.setValue('limparArquivo3', true);
          break;

        case 4:
          setInspecaoObra({ ...inspecaoObra, nomeArquivo4: null });
          hookForm.setValue('limparArquivo4', true);
          break;

        case 5:
          setInspecaoObra({ ...inspecaoObra, nomeArquivo5: null });
          hookForm.setValue('limparArquivo5', true);
          break;
      }
    }
  }, [
    inspecaoObra,
    hookForm,
  ]);

  const handleClearInspecaoObraItem = useCallback((idInspecaoObraItens: number) => {
    const copiaInspecaoObraItens = [...hookForm.getValues().inspecaoObraItens];
    const inspecaoObraItemIndex = copiaInspecaoObraItens.findIndex(x => x.idInspecaoObraItem === idInspecaoObraItens);

    if (inspecaoObraItemIndex >= 0) {
      copiaInspecaoObraItens[inspecaoObraItemIndex].inspecao1 = '';
      copiaInspecaoObraItens[inspecaoObraItemIndex].dataInspecao1 = null;
      copiaInspecaoObraItens[inspecaoObraItemIndex].inspecao2 = '';
      copiaInspecaoObraItens[inspecaoObraItemIndex].dataInspecao2 = null;
    }

    hookForm.setValue('inspecaoObraItens', copiaInspecaoObraItens);
  }, [
    hookForm,
  ]);

  const handleOcorrenciaSubmit = useCallback(async (form: InspecaoObraOcorrenciaForm) => {
    if (idObra && idInspecaoObra) {
      try {
        setSaving(true);
        setPageSaving(true);

        if (selectedOcorrencia) {
          await updateInspecaoObraOcorrenciaUseCase.updateInspecaoObraOcorrencia(
            idObra,
            idInspecaoObra,
            selectedOcorrencia.idInformacoesInspecaoObra,
            form.idItemInspecao,
            form.inspecao,
            form.descricao,
            form.dataDescricao,
            form.tratativa,
            form.dataTratativa,
          );
        } else {
          await createInspecaoObraOcorrenciaUseCase.createInspecaoObraOcorrencia(
            idObra,
            idInspecaoObra,
            form.idItemInspecao,
            form.inspecao,
            form.descricao,
            form.dataDescricao,
            form.tratativa,
            form.dataTratativa,
          );
        }
        await loadOcorrencias();

        setOcorrenciaModal(false);
        setSelectedOcorrencia(undefined);
        setFillOcorrenciaData(false);

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao salvar ocorrência!"));
      } finally {
        setPageSaving(false);
        setSaving(false);
      }
    }
  }, [
    idObra,
    idInspecaoObra,
    selectedOcorrencia,
    createInspecaoObraOcorrenciaUseCase,
    updateInspecaoObraOcorrenciaUseCase,
    loadOcorrencias,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleExecutadoPorModalOpenState = useCallback((openState: boolean) => {
    setExecutadoPorModal(openState);
  }, [
  ]);

  const handleExecutadoPorSubmit = useCallback(async (form: InspecaoObraExecutadoPorForm) => {
    if (idObra && idInspecaoObra) {
      try {
        setSaving(true);
        setPageSaving(true);

        await createInspecaoObraExecutadoPorUseCase.createInspecaoObraExecutadoPor(
          idObra,
          idInspecaoObra,
          form.realizadoPor,
          form.idFornecedor,
          form.funcionarios,
        );

        await loadExecutadoPor();
        setExecutadoPorModal(false);

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao criar executado por!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idObra,
    idInspecaoObra,
    createInspecaoObraExecutadoPorUseCase,
    loadExecutadoPor,
    setPageSaving,
    pushErrorNotification,
    pushInfoNotification,
  ]);

  const handleDeleteOcorrenciaModalOpenState = useCallback((idInformacoesInspecaoObra: number | null, openState: boolean) => {
    if (openState) {
      if (idInformacoesInspecaoObra && inspecaoObraOcorrencias) {
        setSelectedOcorrencia(inspecaoObraOcorrencias.find(x => x.idInformacoesInspecaoObra === idInformacoesInspecaoObra));
      } else {
        setSelectedOcorrencia(undefined);
      }
    }
    setDeleteOcorrenciaModal(openState);
  }, [
    inspecaoObraOcorrencias,
  ]);

  const handleDeleteOcorrenciaSubmit = useCallback(async () => {
    if (idObra && idInspecaoObra && selectedOcorrencia) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deleteInspecaoObraOcorrenciaUseCase.deleteInspecaoObraOcorrencia(
          idObra,
          idInspecaoObra,
          selectedOcorrencia.idInformacoesInspecaoObra,
        );

        await loadOcorrencias();

        setDeleteOcorrenciaModal(false);

        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao excluir ocorrência!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idObra,
    idInspecaoObra,
    selectedOcorrencia,
    deleteInspecaoObraOcorrenciaUseCase,
    loadOcorrencias,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleDeleteExecutadoPorModalOpenState = useCallback((idExecutadoPor: number | null, openState: boolean) => {
    if (openState) {
      if (idExecutadoPor && executadoPor) {
        setSelectedInspecaoObraExecutadoPor(executadoPor.find(x => x.idExecutadoPor === idExecutadoPor));
      } else {
        setSelectedInspecaoObraExecutadoPor(undefined);
      }
    }
    setDeleteExecutadoPorModal(openState);
  }, [
    executadoPor,
  ]);

  const handleDeleteExecutadoPorSubmit = useCallback(async () => {
    if (idObra && idInspecaoObra && selectedInspecaoObraExecutadoPor) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deleteInspecaoObraExecutadoPorUseCase.deleteInspecaoObraExecutadoPor(
          idObra,
          idInspecaoObra,
          selectedInspecaoObraExecutadoPor.idExecutadoPor,
        );

        await loadExecutadoPor();

        setDeleteExecutadoPorModal(false);

        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao excluir executado por!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idObra,
    idInspecaoObra,
    selectedInspecaoObraExecutadoPor,
    deleteInspecaoObraExecutadoPorUseCase,
    loadExecutadoPor,
    setPageSaving,
    pushErrorNotification,
    pushInfoNotification,
  ]);

  const handleInspecaoObraItensPageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useObraItensChecklistAreaVerificacaoPagination.getState().inspecaoObraItensSetter.setPage(page + 1);
      await loadInspecaoObraItens({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar itens de inpeção de obra!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadInspecaoObraItens,
    pushErrorNotification,
  ]);

  const handleInspecaoObraItensRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useObraItensChecklistAreaVerificacaoPagination.getState().inspecaoObraItensSetter.setPage(1);
      useObraItensChecklistAreaVerificacaoPagination.getState().inspecaoObraItensSetter.setRowsPerPage(rowsPerPage);
      await loadInspecaoObraItens({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar itens de inpeção de obra!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadInspecaoObraItens,
    pushErrorNotification,
  ]);

  const handleInspecaoObraOcorrenciaPageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useObraItensChecklistAreaVerificacaoPagination.getState().ocorrenciasSetter.setPage(page + 1);
      await loadOcorrencias({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar itens de inpeção de obra!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadOcorrencias,
    pushErrorNotification,
  ]);

  const handleInspecaoObraOcorrenciaRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useObraItensChecklistAreaVerificacaoPagination.getState().ocorrenciasSetter.setPage(1);
      useObraItensChecklistAreaVerificacaoPagination.getState().ocorrenciasSetter.setRowsPerPage(rowsPerPage);
      await loadOcorrencias({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar itens de inpeção de obra!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadOcorrencias,
    pushErrorNotification,
  ]);

  const handleExecutadoPorPageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useObraItensChecklistAreaVerificacaoPagination.getState().executadoPorSetter.setPage(page + 1);
      await loadExecutadoPor({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar itens de inpeção de obra!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadExecutadoPor,
    pushErrorNotification,
  ]);

  const handleExecutadoPorRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useObraItensChecklistAreaVerificacaoPagination.getState().executadoPorSetter.setPage(1);
      useObraItensChecklistAreaVerificacaoPagination.getState().executadoPorSetter.setRowsPerPage(rowsPerPage);
      await loadExecutadoPor({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar itens de inpeção de obra!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadExecutadoPor,
    pushErrorNotification,
  ]);

  const handleOpenChangeHistory = useCallback((
    idTabela: number | null,
    tipoTabela: LogTipoTabela | null,
  ) => {
    if (idTabela && tipoTabela) {
      changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
    } else {
      if (idInspecaoObra) {
        changeHistory.handleOpenChangeHistory(idInspecaoObra, LogTipoTabela.INSPECAO_OBRA, true);
      }
    }
  }, [
    idInspecaoObra,
    changeHistory,
  ]);

  const handleFillCurrentDateChange = useCallback((value: boolean) => {
    setFillCurrentDate(value);
  }, [
  ]);

  return {
    hookForm,
    loading,
    saving,
    errorMessage,
    atributoObras: atributoObras ?? null,
    fillOcorrenciaData: fillOcorrenciaData ?? false,
    lockFuncionarioInspecionado,
    lockFuncionarioAprovado,
    funcionariosDropdown: funcionariosDropDown ?? [],
    filteredFuncionariosDropdown: filteredFuncionariosDropdown ?? [],
    fornecedoresDropDown: fornecedoresDropDown ?? [],
    funcionariosTercDropDown: funcionariosTercDropDown ?? [],
    idObra,
    idObraAreaChecklist,
    idObraChecklistServico,
    idInspecaoObra,
    inspecaoObra: inspecaoObra ?? null,
    inspecoesObraItens: inspecaoObraItens,
    inspecoesObraItensBase: inspecaoObraItensBase ?? [],
    executadoPor: executadoPor ?? [],
    inspecaoObraOcorrencias: inspecaoObraOcorrencias ?? [],
    ocorrenciaModal,
    executadoPorModal,
    deleteOcorrenciaModal,
    deleteExecutadoPorModal,
    selectedOcorrencia: selectedOcorrencia ?? null,
    selectedInspecaoObraItem: selectedInspecaoObraItem ?? null,
    estatisticasChecklistServico: estatisticasChecklistServico ?? null,
    obraChecklistServicoCampos: obraChecklistServicoCampos ?? null,
    obraChecklistServicoHeader,
    obrasTopHeaderState,
    obrasReportState,
    selectedObraHeaderInfo,
    pagination,
    changeHistory,
    lockInspecaoObraItens,
    handleBack,
    handleReload,
    handleSubmit: hookForm.handleSubmit(handleSubmit),
    handleOcorrenciaModalOpenState,
    handleInspecao1Change,
    handleDataInspecao1Change,
    handleInspecao2Change,
    handleDataInspecao2Change,
    handleDownloadFile,
    handleDeleteFile,
    handleClearInspecaoObraItem,
    handleOcorrenciaSubmit,
    handleExecutadoPorModalOpenState,
    handleExecutadoPorSubmit,
    handleDeleteOcorrenciaModalOpenState,
    handleDeleteOcorrenciaSubmit,
    handleDeleteExecutadoPorModalOpenState,
    handleDeleteExecutadoPorSubmit,
    handleInspecaoObraItensPageChange,
    handleInspecaoObraItensRowsPerPageChange,
    handleInspecaoObraOcorrenciaPageChange,
    handleInspecaoObraOcorrenciaRowsPerPageChange,
    handleExecutadoPorPageChange,
    handleExecutadoPorRowsPerPageChange,
    handleOpenChangeHistory,
    handleFillCurrentDateChange,
  };
}
